import { createFileRoute } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useEffect, useMemo, lazy, Suspense } from 'react';
import { getWebApp } from '@/lib/telegram';
import { Crown, Users, TrendingUp, Wallet, AlertTriangle, Activity } from 'lucide-react';
import { LiveFeed } from '@/components/LiveFeed';
import { Skeleton } from '@/components/ui/skeleton';

const PnlChart = lazy(() => import('@/components/PnlChart'));

export const Route = createFileRoute('/')({ component: Platform });

function Platform() {
  const { t } = useTranslation();

  useEffect(() => {
    const tg = getWebApp();
    tg?.BackButton.hide();
    tg?.MainButton.hide();
  }, []);

  const { data: partners, isPending: partnersPending } = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.partners(),
    placeholderData: keepPreviousData,
  });
  const { data: withdraws } = useQuery({
    queryKey: ['withdraws'],
    queryFn: () => api.pendingWithdraws(),
    placeholderData: keepPreviousData,
  });
  const { data: events } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => api.webhookEvents(),
    placeholderData: keepPreviousData,
  });
  const { data: pnl } = useQuery({
    queryKey: ['ownerPnl'],
    queryFn: () => api.ownerPnl(),
    placeholderData: keepPreviousData,
  });
  const { data: retention } = useQuery({
    queryKey: ['retention'],
    queryFn: () => api.retention(8),
    placeholderData: keepPreviousData,
  });
  // Живой курс USD→RUB (CBR) для конверсии USD-cents в ₽.
  const { data: fxRates } = useQuery({ queryKey: ['fxRates'], queryFn: () => api.fxRates() });
  const usdToRub = useMemo(() => fxRates?.find((r) => r.code === 'RUB')?.perRub ?? 100, [fxRates]);
  // Честные счётчики с бэка (COUNT). Раньше брали .length от списка партнёров,
  // который обрезан LIMIT 100 → «Активные партнёры: 100» при реально большем числе.
  const { data: netStats } = useQuery({
    queryKey: ['networkStats'],
    queryFn: () => api.networkStats(),
    placeholderData: keepPreviousData,
  });
  const { data: refStats } = useQuery({
    queryKey: ['ownerReferrals'],
    queryFn: () => api.ownerReferralStats(),
    placeholderData: keepPreviousData,
  });

  const activePartners =
    netStats?.active_partners ?? partners?.filter((p) => p.status === 'active').length ?? 0;
  const activeSubs =
    netStats?.active_subscriptions ?? partners?.reduce((s, p) => s + p.activeSubs, 0) ?? 0;
  const pendingWithdraws = withdraws?.length ?? 0;
  const failedWebhooks = events?.filter((e) => e.status === 'failed').length ?? 0;

  const chart = pnl?.chart ?? [];
  // gmv/margin приходят в USD-cents → в ₽ по живому курсу, затем в тысячи (K).
  const gmvTodayMinor = chart.length > 0 ? chart[chart.length - 1].gmv : 0;
  const marginTodayMinor = chart.length > 0 ? chart[chart.length - 1].margin : 0;
  const gmvTodayKRub = (gmvTodayMinor * usdToRub) / 100 / 1000;
  const marginTodayKRub = (marginTodayMinor * usdToRub) / 100 / 1000;

  const topEarners = [...(partners || [])]
    .sort((a, b) => b.totalEarnedUsdMinor - a.totalEarnedUsdMinor)
    .slice(0, 5);

  if (partnersPending && !partners) return <PlatformSkeleton />;

  return (
    <motion.div initial={false} className="p-4 space-y-6 pb-24">
      {/* Block F.6: SSE live feed на топе */}
      <LiveFeed />

      <div className="px-2 pt-2 flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{t('Admin')}</p>
          <h2 className="text-2xl font-bold tracking-tight">{t('Platform')}</h2>
        </div>
        <div className="flex items-center gap-1.5 bg-green-500/15 text-green-500 px-3 py-1.5 rounded-full">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-[11px] font-bold uppercase tracking-wider">{t('Online')}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={<Crown />} label="Активные партнёры" value={String(activePartners)} />
        <StatCard icon={<Users />} label="Активные подписки" value={String(activeSubs)} />
        <StatCard
          icon={<TrendingUp />}
          label="Оборот сегодня"
          value={`${gmvTodayKRub.toFixed(1)}K ₽`}
          tooltip="GMV — сколько клиенты заплатили партнёрам сегодня в розничных ценах."
        />
        <StatCard
          icon={<Wallet />}
          label="Маржа платформы"
          value={`${marginTodayKRub.toFixed(1)}K ₽`}
          tooltip="Наш доход после партнёрских отчислений (до комиссий платёжек)."
        />
      </div>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 px-2">Оборот · 14 дней</h3>
          <div className="h-44">
            <Suspense fallback={<Skeleton className="w-full h-full rounded-md" />}>
              <PnlChart data={chart} />
            </Suspense>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <AlertCard
          icon={<Wallet className="w-5 h-5" />}
          label={t('Pending withdraws')}
          value={pendingWithdraws}
          accent={pendingWithdraws > 0 ? 'yellow' : 'default'}
        />
        <AlertCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label={t('Failed webhooks')}
          value={failedWebhooks}
          accent={failedWebhooks > 0 ? 'red' : 'default'}
        />
      </div>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3 px-2 flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" />
            {t('Top earners')}
          </h3>
          <div className="space-y-1">
            {topEarners.map((p, i) => (
              <div key={p.id} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-white/[0.02]">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-6 text-center font-mono text-muted-foreground text-sm">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {p.firstName} {p.username && <span className="text-muted-foreground">@{p.username}</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">{p.activeSubs} subs · {p.botsCount} bots</div>
                  </div>
                </div>
                <div className="font-bold text-sm shrink-0">{Math.round((p.totalEarnedUsdMinor * usdToRub) / 100).toLocaleString('ru')} ₽</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {refStats && refStats.totalReferred > 0 && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <h3 className="font-semibold px-2 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Рефералы сети
            </h3>
            <div className="grid grid-cols-2 gap-3 px-2">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Приглашено</div>
                <div className="text-xl font-bold">
                  {refStats.totalReferred}
                  <span className="text-sm text-muted-foreground"> · {refStats.lockedReferred} закр.</span>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Выплачено бонусов</div>
                <div className="text-xl font-bold">
                  {Math.round((refStats.totalBonusPaidMinor * usdToRub) / 100).toLocaleString('ru')} ₽
                </div>
              </div>
            </div>
            {refStats.topReferrers.length > 0 && (
              <div className="space-y-1 border-t border-white/5 pt-2">
                {refStats.topReferrers.slice(0, 5).map((r, i) => (
                  <div key={r.clientId} className="flex items-center justify-between px-2 py-1.5 text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-5 text-center font-mono text-muted-foreground text-xs">{i + 1}</span>
                      <span className="truncate">
                        {r.tgUsername ? `@${r.tgUsername}` : `id ${r.tgUserId}`}
                        <span className="text-muted-foreground text-xs"> · {r.referredCount} чел.</span>
                      </span>
                    </div>
                    <span className="font-bold shrink-0">
                      {Math.round((r.bonusEarnedMinor * usdToRub) / 100).toLocaleString('ru')} ₽
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {retention && retention.cohorts.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between px-2">
              <h3 className="font-semibold">Retention (D7 / D30)</h3>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-primary font-bold">{retention.overall_d7_percent.toFixed(0)}% D7</span>
                <span className="text-green-500 font-bold">{retention.overall_d30_percent.toFixed(0)}% D30</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-1 px-2 text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                <div className="col-span-4">Когорта</div>
                <div className="col-span-3 text-right">Signups</div>
                <div className="col-span-2 text-right">D7</div>
                <div className="col-span-3 text-right">D30</div>
              </div>
              {retention.cohorts.map((c) => {
                const d7Pct = c.signups > 0 ? Math.round((c.active_d7 / c.signups) * 100) : 0;
                const d30Pct = c.signups > 0 ? Math.round((c.active_d30 / c.signups) * 100) : 0;
                return (
                  <div key={c.cohort_week} className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center text-sm">
                    <div className="col-span-4 font-mono text-xs text-muted-foreground truncate">{c.cohort_week}</div>
                    <div className="col-span-3 text-right font-semibold">{c.signups}</div>
                    <div className="col-span-2 text-right">
                      {c.active_d7 > 0 ? <span className="text-primary font-semibold">{d7Pct}%</span> : <span className="text-muted-foreground">—</span>}
                    </div>
                    <div className="col-span-3 text-right">
                      {c.active_d30 > 0 ? <span className="text-green-500 font-semibold">{d30Pct}%</span> : <span className="text-muted-foreground">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Geo top — нет /api/owner/analytics/geo (требует геоданных в Client),
          можно добавить позже когда появится geo-derivation из IP/phone. */}
    </motion.div>
  );
}

function StatCard({
  icon,
  label,
  value,
  tooltip,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tooltip?: string;
}) {
  return (
    <Card className="bg-card border-white/5" title={tooltip}>
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span className="text-primary [&_svg]:w-4 [&_svg]:h-4">{icon}</span>
          <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
        </div>
        <div className="text-xl font-bold truncate">{value}</div>
      </CardContent>
    </Card>
  );
}

function AlertCard({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: number; accent: 'yellow' | 'red' | 'default' }) {
  const colorCls =
    accent === 'red'
      ? 'border-red-500/30 bg-red-500/5 text-red-500'
      : accent === 'yellow'
        ? 'border-yellow-500/30 bg-yellow-500/5 text-yellow-500'
        : 'border-white/5 bg-card text-muted-foreground';
  return (
    <Card className={`border ${colorCls}`}>
      <CardContent className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-2">{icon}<span className="text-xs font-semibold uppercase tracking-wider">{label}</span></div>
        <div className="text-2xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function PlatformSkeleton() {
  return (
    <div className="p-4 space-y-6 pb-24">
      <div className="px-2 pt-2 space-y-2">
        <Skeleton className="h-7 w-44" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
      <Skeleton className="h-56 rounded-xl" />
      <Skeleton className="h-44 rounded-xl" />
    </div>
  );
}
